import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, FormControlLabel, IconButton, Paper, Stack, Switch, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchableSelect from '../common/SearchableSelect';
import { ProvidentFundService } from '../../services/providentFund.service';
import { PfEmployerBasis } from '../../interfaces/providentFund.interface';
import { helpWhy } from '../../help/why';
import { useToast } from '../../context/ToastContext';

interface Props {
    institutionId: string;
    canManage: boolean;
}

interface StepForm { yearsOfService: string; vestedPercent: string }

const BASES: { id: PfEmployerBasis; label: string }[] = [
    { id: 'MATCH_EMPLOYEE', label: 'Match the employee share' },
    { id: 'PERCENT_OF_BASIC', label: 'Percent of basic salary' },
];

// W3-J: provident-fund settings: opt-in, deduction component, employer basis, eligibility and vesting steps.
const PfSettingsTab: React.FC<Props> = ({ institutionId, canManage }) => {
    const { showSuccess, showApiError } = useToast();
    const [enabled, setEnabled] = useState(false);
    const [component, setComponent] = useState('');
    const [basis, setBasis] = useState<PfEmployerBasis>('MATCH_EMPLOYEE');
    const [percent, setPercent] = useState('');
    const [eligibility, setEligibility] = useState('0');
    const [steps, setSteps] = useState<StepForm[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!institutionId) return;
        setLoading(true);
        try {
            const s = await ProvidentFundService.getSettings(institutionId);
            setEnabled(!!s.enabled);
            setComponent(s.deductionComponentName ?? '');
            setBasis(s.employerBasis ?? 'MATCH_EMPLOYEE');
            setPercent(s.employerPercent === null || s.employerPercent === undefined ? '' : String(s.employerPercent));
            setEligibility(String(s.eligibilityMonths ?? 0));
            setSteps((s.vestingSteps ?? []).map(v => ({ yearsOfService: String(v.yearsOfService), vestedPercent: String(v.vestedPercent) })));
        } catch (e) {
            showApiError(e, 'Failed to load the provident fund settings');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    useEffect(() => { load(); }, [load]);

    const stepsValid = steps.every(s => s.yearsOfService !== '' && s.vestedPercent !== ''
        && Number(s.vestedPercent) >= 0 && Number(s.vestedPercent) <= 100 && Number(s.yearsOfService) >= 0);
    // The server needs a percentage above 0 when the employer share is a percent of basic.
    const percentValid = basis !== 'PERCENT_OF_BASIC' || (percent !== '' && Number(percent) > 0 && Number(percent) <= 100);
    const valid = component.trim() !== '' && eligibility !== '' && Number(eligibility) >= 0
        && percentValid && stepsValid;

    const saveWhy = saving ? 'Saving is in progress.'
        : component.trim() === '' ? 'Type the deduction component name first.'
        : !percentValid ? 'Enter an employer percent above 0 and up to 100.'
        : !valid ? 'Fix the highlighted values first.'
        : !institutionId ? 'No institution is selected.' : null;

    const save = async () => {
        setSaving(true);
        try {
            await ProvidentFundService.updateSettings(institutionId, {
                enabled,
                deductionComponentName: component.trim(),
                employerBasis: basis,
                employerPercent: basis === 'PERCENT_OF_BASIC' ? Number(percent) : (percent === '' ? null : Number(percent)),
                eligibilityMonths: Number(eligibility),
                vestingSteps: steps.map(s => ({ yearsOfService: Number(s.yearsOfService), vestedPercent: Number(s.vestedPercent) })),
            });
            showSuccess('Provident fund settings saved');
            await load();
        } catch (e) {
            showApiError(e, 'Failed to save the provident fund settings');
        } finally {
            setSaving(false);
        }
    };

    const setStep = (i: number, patch: Partial<StepForm>) =>
        setSteps(list => list.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

    return (
        <Paper data-help-page="payroll.pfSettings.page" variant="outlined" sx={{ p: 2 }}>
            <FormControlLabel control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} disabled={!canManage} />}
                data-help="payroll.pfSettings.field.enabled" label="Provident fund enabled" />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                <TextField data-help="payroll.pfSettings.field.component" label="Deduction component name" value={component} onChange={e => setComponent(e.target.value)} disabled={!canManage}
                    helperText="The salary deduction component that carries the employee share." inputProps={{ maxLength: 100 }}
                    sx={{ flex: '1 1 260px', maxWidth: 400 }} />
                <SearchableSelect data-help="payroll.pfSettings.field.basis" label="Employer contribution" options={BASES} value={basis} clearable={false} disabled={!canManage}
                    onChange={id => id && setBasis(id as PfEmployerBasis)} getOptionLabel={o => o.label}
                    sx={{ flex: '1 1 260px', maxWidth: 400 }} />
                <TextField data-help="payroll.pfSettings.field.percent" label="Employer percent" type="number" value={percent} onChange={e => setPercent(e.target.value)}
                    disabled={!canManage || basis !== 'PERCENT_OF_BASIC'} inputProps={{ min: 0, max: 100, step: '0.01' }}
                    error={!percentValid} helperText={percentValid ? undefined : 'Must be above 0 and up to 100'}
                    sx={{ flex: '1 1 160px', maxWidth: 220 }} />
                <TextField data-help="payroll.pfSettings.field.eligibility" label="Eligibility (months of service)" type="number" value={eligibility} onChange={e => setEligibility(e.target.value)}
                    disabled={!canManage} inputProps={{ min: 0, max: 120 }} sx={{ flex: '1 1 200px', maxWidth: 260 }} />
            </Box>

            <Typography data-help-section="payroll.pfSettings.section.vesting" variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Vesting of the employer share</Typography>
            {steps.length === 0 && <Alert severity="info" sx={{ mb: 1 }}>No vesting steps: the employer share is not vested until you add steps.</Alert>}
            <Stack spacing={1}>
                {steps.map((s, i) => (
                    <Box key={i} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        <TextField data-help="payroll.pfSettings.field.stepYears" label="From years of service" type="number" size="small" value={s.yearsOfService} disabled={!canManage}
                            onChange={e => setStep(i, { yearsOfService: e.target.value })} inputProps={{ min: 0, max: 80 }}
                            sx={{ flex: '1 1 180px', maxWidth: 240 }} />
                        <TextField data-help="payroll.pfSettings.field.stepPercent" label="Vested %" type="number" size="small" value={s.vestedPercent} disabled={!canManage}
                            onChange={e => setStep(i, { vestedPercent: e.target.value })} inputProps={{ min: 0, max: 100 }}
                            sx={{ flex: '1 1 140px', maxWidth: 200 }} />
                        {canManage && (
                            <IconButton data-help="payroll.pfSettings.removeStep" aria-label="Remove step" onClick={() => setSteps(list => list.filter((_, idx) => idx !== i))}>
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </Box>
                ))}
            </Stack>
            {canManage && (
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                    <Button data-help="payroll.pfSettings.addStep" startIcon={<AddIcon />} onClick={() => setSteps(list => [...list, { yearsOfService: '', vestedPercent: '' }])}>Add step</Button>
                    <Button data-help="payroll.pfSettings.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" onClick={save} disabled={!valid || saving || !institutionId}>Save</Button>
                </Box>
            )}
        </Paper>
    );
};

export default PfSettingsTab;
